import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import firebase, { auth } from './firebaseConfig';

const AuthContext = createContext({ user: null, loading: true, refreshUser: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Profile updates (e.g. displayName) mutate the Firebase user in place
  // without firing onAuthStateChanged; bumping this re-renders consumers.
  const [, setVersion] = useState(0);
  const refreshUser = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!firebase.apps.length) {
      setLoading(false);
      return undefined;
    }
    const unsub = auth.onAuthStateChanged((u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function signUp(email, password) {
  return auth.createUserWithEmailAndPassword(email, password);
}

export function signIn(email, password) {
  return auth.signInWithEmailAndPassword(email, password);
}

export function signOut() {
  return auth.signOut();
}

export function resetPassword(email) {
  return auth.sendPasswordResetEmail(email);
}

// Firebase requires a recent sign-in before sensitive changes, so we
// re-authenticate with the current password first.
export async function changePassword(currentPassword, newPassword) {
  const user = auth.currentUser;
  const cred = firebase.auth.EmailAuthProvider.credential(user.email, currentPassword);
  await user.reauthenticateWithCredential(cred);
  await user.updatePassword(newPassword);
}

export async function updateDisplayName(name) {
  await auth.currentUser.updateProfile({ displayName: name });
}
